// src/pages/AboutPage.jsx
import { Link } from "react-router-dom";
import { useSiteContent } from "../hooks/useSiteContent";
import "./AboutPage.css";

const TargetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);

const ZapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

const LeafIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/>
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
  </svg>
);

const HandshakeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="m11 17 2 2a1 1 0 1 0 3-3"/>
    <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4"/>
    <path d="m21 3 1 11h-2"/>
    <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3"/>
    <path d="M3 4h8"/>
  </svg>
);

const values = [
  { Icon: TargetIcon, title: "Quality First", desc: "We never compromise on materials, colour accuracy, or finishing. Every job goes through a rigorous quality check before dispatch." },
  { Icon: ZapIcon, title: "Speed & Reliability", desc: "We understand deadlines are real. Same-day dispatch, express options, and tracked delivery keep your project on schedule." },
  { Icon: LeafIcon, title: "Sustainability", desc: "We use FSC-certified paper stocks and are committed to reducing our environmental footprint year on year." },
  { Icon: HandshakeIcon, title: "Partnership", desc: "We're not just a printer — we're a partner. We offer free artwork advice and dedicated support on every order." },
];

export default function AboutPage() {
  const content = useSiteContent();
  return (
    <div className="about-page">
      {/* Hero */}
      <div className="about-hero">
        <div className="container anim-fade-in-up">
          <span className="tag">Our Story</span>
          <h1 className="section-title" style={{ color: "white" }}>About Circuit<br/>Printing Solutions</h1>
          <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 17, marginTop: 12, maxWidth: 560 }}>
            {content.aboutHeroText}
          </p>
        </div>
      </div>

      {/* Intro */}
      <section className="about-intro">
        <div className="container about-intro-inner">
          <div className="reveal-left">
            <span className="tag">Who We Are</span>
            <h2 className="section-title">{content.aboutIntroTitle.split("\n").map((line, i) => <span key={line}>{i > 0 && <br />}{line}</span>)}</h2>
            <p>{content.aboutIntroParagraph1}</p>
            <p>{content.aboutIntroParagraph2}</p>
            <Link to="/contact" className="btn-primary" style={{ marginTop: 24, display: "inline-block" }}>Work With Us</Link>
          </div>
          <div className="about-cmyk-visual reveal-right">
            <div className="cmyk-block c">C</div>
            <div className="cmyk-block m">M</div>
            <div className="cmyk-block y">Y</div>
            <div className="cmyk-block k">K</div>
            <p>CMYK Precision<br/>Colour Printing</p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="about-stats">
        <div className="container about-stats-grid">
          {[
            { num: "25+", label: "Years in Business" },
            { num: "400K+", label: "Orders Completed" },
            { num: "500+", label: "Products Available" },
            { num: "99%", label: "On-Time Delivery Rate" },
          ].map((s, i) => (
            <div key={s.label} className={`about-stat reveal delay-${i + 1}`}>
              <strong>{s.num}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="about-values">
        <div className="container">
          <div className="section-header center reveal">
            <span className="tag">What Drives Us</span>
            <h2 className="section-title">Our Values</h2>
          </div>
          <div className="values-grid">
            {values.map(({ Icon, title, desc }, i) => (
              <div key={title} className={`value-card reveal delay-${(i % 6) + 1}`}>
                <span className="value-icon"><Icon /></span>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container about-cta-inner reveal-zoom">
          <h2>Start Your Print Project Today</h2>
          <p>Browse our full range of print products or get in touch for a custom quote.</p>
          <div style={{ display: "flex", gap: 14, marginTop: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <Link to="/products" className="btn-primary">Browse Products</Link>
            <Link to="/contact" className="btn-outline">Request a Quote</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
